import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useFavorites } from '../context/FavoritesContext';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config';
import './HomeScreen.css';

const CHECKED_COUNT_KEY = 'poupix_checked_count';
const getCheckedCount = () => {
  try { return Math.max(0, parseInt(localStorage.getItem(CHECKED_COUNT_KEY) || '0', 10)); }
  catch (_) { return 0; }
};

const LEVEL_TITLES = ['Curioso', 'Atento', 'Comparador', 'Caçador', 'Poupador', 'Mestre'];
const SAVINGS_GOAL = 30;

const RECENTLY_VIEWED_KEY = 'poupix_recent';

const getRecentIds = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY) || '[]')
      .map((item) => Number(item?.id))
      .filter((id) => Number.isInteger(id) && id > 0)
      .slice(0, 6);
  } catch (_) { return []; }
};


export default function HomeScreen({ onSearch, onCategorySelect, onCartClick, onScanClick, onSearchClick }) {
  const navigate = useNavigate();
  const { cartItems } = useCart();
  const { favorites } = useFavorites();
  const { user } = useAuth();
  const [recentProducts, setRecentProducts] = useState([]);
  const localCheckedCount = useState(() => getCheckedCount())[0];
  // Prefer server-side scan count for logged-in users (only barcode scans count).
  const checkedCount = user?.scan_count ?? localCheckedCount;

  useEffect(() => {
    const fetch = async () => {
      try {
        const recentIds = getRecentIds();
        const res = await axios.get(`${API_BASE_URL}/products/homepage`, {
          timeout: 12000,
          params: recentIds.length ? { recent_ids: recentIds.join(',') } : undefined,
        });
        setRecentProducts(Array.isArray(res.data?.recent_products) ? res.data.recent_products : []);
      } catch (_) {}
    };
    fetch();
  }, []);

  const cartCount = useMemo(() => cartItems.reduce((s, i) => s + i.quantity, 0), [cartItems]);

  const level = Math.min(Math.floor(checkedCount / 10), 5);
  const levelTitle = LEVEL_TITLES[level];
  const levelProgress = ((checkedCount % 10) / 10) * 100;
  const toNextLevel = 10 - (checkedCount % 10);

  const { totalSavings, savingsCount } = useMemo(() => {
    let total = 0, count = 0;
    favorites.forEach((fav) => {
      if (fav.best_price != null && fav.price_when_favorited != null) {
        const saving = fav.price_when_favorited - fav.best_price;
        if (saving > 0.005) { total += saving; count++; }
      }
    });
    return { totalSavings: total, savingsCount: count };
  }, [favorites]);
  const savingsProgress = Math.min((totalSavings / SAVINGS_GOAL) * 100, 100);

  return (
    <div className="hs">
      {/* iOS large-title header */}
      <div className="hs-header">
        <div>
          <div className="hs-greeting">Poupix</div>
          <div className="hs-title">Hoje</div>
        </div>
      </div>

      {/* Hero: savings from tracked favorites */}
      <button className="hs-hero hs-hero--new" onClick={() => navigate('/favorites')}>
        <div className="hs-hero-bubble hs-hero-bubble--1" aria-hidden="true" />
        <div className="hs-hero-bubble hs-hero-bubble--2" aria-hidden="true" />
        <div className="hs-hero-row-top">
          <span className="hs-hero-eyebrow">Nos seus favoritos</span>
          <svg viewBox="0 0 24 24" fill="none" width="14" height="14" style={{ opacity: 0.5 }}>
            <path d="M9 6l6 6-6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>

        {totalSavings > 0.01 ? (
          <>
            <div className="hs-hero-savings-label">Poupança acumulada</div>
            <div className="hs-hero-count-row">
              <span className="hs-hero-count">€{totalSavings.toFixed(2).replace('.', ',')}</span>
              <span className="hs-hero-count-label">
                em {savingsCount} produto{savingsCount !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="hs-hero-progress-track">
              <div className="hs-hero-progress-fill" style={{ width: `${savingsProgress}%` }} />
            </div>
            <div className="hs-hero-progress-labels">
              <span>Meta · €{SAVINGS_GOAL}</span>
              {totalSavings < SAVINGS_GOAL && (
                <span>+€{(SAVINGS_GOAL - totalSavings).toFixed(2).replace('.', ',')} para a meta</span>
              )}
            </div>
          </>
        ) : (
          <div className="hs-hero-count-row">
            <span className="hs-hero-count">{favorites.length}</span>
            <span className="hs-hero-count-label">
              produto{favorites.length !== 1 ? 's' : ''} a vigiar<br />
              <span style={{ opacity: 0.65, fontSize: 12 }}>em 4 supermercados</span>
            </span>
          </div>
        )}

        {favorites.length > 0 && (
          <div className="hs-hero-pills">
            {[...favorites]
              .filter((fav) => fav.best_price != null)
              .sort((a, b) => {
                const da = a.price_when_favorited != null ? a.price_when_favorited - a.best_price : 0;
                const db = b.price_when_favorited != null ? b.price_when_favorited - b.best_price : 0;
                return db - da;
              })
              .slice(0, 3)
              .map((fav) => {
                const prev = fav.price_when_favorited;
                const down = prev != null && fav.best_price < prev - 0.005;
                const up = prev != null && fav.best_price > prev + 0.005;
                return (
                  <div key={fav.id} className="hs-hero-pill">
                    {down && <span className="hs-hero-pill-arrow">↓</span>}
                    {up && <span className="hs-hero-pill-arrow hs-hero-pill-arrow--up">↑</span>}
                    <span className="hs-hero-pill-name">{fav.name?.split(' ')[0]}</span>
                    {down && prev
                      ? <span className="hs-hero-pill-price">−€{(prev - fav.best_price).toFixed(2).replace('.', ',')}</span>
                      : <span className="hs-hero-pill-price">€{Number(fav.best_price).toFixed(2).replace('.', ',')}</span>
                    }
                  </div>
                );
              })}
          </div>
        )}
      </button>

      {/* Products checked / level gamification card */}
      <div className="hs-streak-card">
        <div className="hs-streak-icon">
          <svg viewBox="0 0 24 24" fill="none" width="22" height="22">
            <path d="M12 2c-1 3-4 5-4 8a4 4 0 008 0c0-3-3-5-4-8z" fill="#C85A00" stroke="none"/>
            <path d="M8.5 14c-.5 2 1 4 3.5 4s4-2 3.5-4" fill="#FF8C00" stroke="none"/>
          </svg>
          <div className="hs-streak-badge">{checkedCount}</div>
        </div>
        <div className="hs-streak-info">
          <div className="hs-streak-title">{checkedCount} produto{checkedCount !== 1 ? 's' : ''} verificado{checkedCount !== 1 ? 's' : ''}</div>
          <div className="hs-streak-level">Nível {level + 1} · <span className="hs-streak-level-name">{levelTitle}</span></div>
          <div className="hs-streak-bar">
            <div className="hs-streak-bar-fill" style={{ width: `${levelProgress}%` }} />
          </div>
        </div>
        <div className="hs-streak-next">
          <div className="hs-streak-next-label">PRÓX.</div>
          <div className="hs-streak-next-days">{toNextLevel}</div>
        </div>
      </div>

      {/* Action tiles */}
      <div className="hs-actions">
        {/* Primary — scan */}
        <button className="hs-tile hs-tile--primary" onClick={onScanClick}>
          <div className="hs-tile-icon">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M3 7V5a2 2 0 012-2h2M21 7V5a2 2 0 00-2-2h-2M3 17v2a2 2 0 002 2h2M21 17v2a2 2 0 01-2 2h-2"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M7 8v8M10 8v8M13 8v8M16 8v8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="hs-tile-body">
            <div className="hs-tile-label">Apontar à câmara</div>
            <div className="hs-tile-sub">Preço mais baixo em 4 supermercados</div>
          </div>
        </button>

        {/* Secondary — search + cart */}
        <div className="hs-tiles-col">
          <button className="hs-tile hs-tile--sm" onClick={() => { if (onSearchClick) onSearchClick(); else navigate('/products'); }}>
            <div className="hs-tile-icon hs-tile-icon--sm">
              <svg viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/>
                <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div>
              <div className="hs-tile-label">Pesquisar</div>
              <div className="hs-tile-sub">por nome ou EAN</div>
            </div>
          </button>
          <button className="hs-tile hs-tile--sm" onClick={onCartClick}>
            <div className="hs-tile-icon hs-tile-icon--sm">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M3 4h2.5l2.4 11.2a2 2 0 002 1.6h7.4a2 2 0 002-1.5L21 8H6"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="9" cy="20" r="1.4" fill="currentColor"/>
                <circle cx="17" cy="20" r="1.4" fill="currentColor"/>
              </svg>
            </div>
            <div>
              <div className="hs-tile-label">Carrinho</div>
              <div className="hs-tile-sub">
                {cartCount > 0 ? `${cartCount} unidades` : 'Vazio'}
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Recently viewed */}
      {recentProducts.length > 0 && (
        <>
          <div className="hs-section-header">
            <div className="hs-section-title">Vistos recentemente</div>
          </div>
          <div className="hs-recent">
            {recentProducts.slice(0, 4).map((product) => (
              <button
                key={product.id}
                className="hs-recent-row"
                onClick={() => navigate(`/product/${product.id}`)}
              >
                <div className="hs-recent-img">
                  {product.image_url && (
                    <img src={product.image_url} alt={product.name} loading="lazy" />
                  )}
                </div>
                <div className="hs-recent-info">
                  <div className="hs-recent-name">{product.name}</div>
                  {product.cheapest_price != null && (
                    <div className="hs-recent-price">
                      €{Number(product.cheapest_price).toFixed(2).replace('.', ',')}
                    </div>
                  )}
                </div>
                <svg className="hs-recent-chevron" viewBox="0 0 24 24" fill="none">
                  <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            ))}
          </div>
        </>
      )}

      <div style={{ height: 112 }} />
    </div>
  );
}
